import { gql } from "@apollo/client";

// The Clients tab is backed by the Client entity (the relationship-level
// wrapper — issue #31) with People as the contact-level identity beneath it.
// Membership is M:N through client_people (1:1 is the common case); each
// client has ONE primary person — its main contact (a membership-level
// isPrimary flag). Stats and billing history live on the Client; notes and
// tags stay per-person.

// ── Shared field sets ────────────────────────────────────────────────

const PERSON_ROW_FIELDS = `
      id
      name
      alias
      email
      phoneNumber
      businessName
      title
      archivedAt
      createdAt
      primaryClientId
      clients {
        id
        name
        alias
        status
        isPrimary
      }
      tags {
        id
        name
        slug
      }
`;

// ── Queries ──────────────────────────────────────────────────────────

/**
 * Paginated client list query. Server-side search (q — matches client
 * name/alias AND member people's name/alias/email/phone), sort, archive
 * filtering, and tag filtering (tagSlugs — clients with a member carrying
 * ALL selected tags).
 *
 * Computed stats (peopleCount, sessionsCount, lifetimeSpend, lastSessionAt,
 * nextSessionAt) aggregate over the Client-stable attribution columns
 * (bookings.client_id / payments.client_id).
 */
/**
 * Per-tab counts for the Clients header, as four aliases of the existing meta
 * field — no API change. Each count carries the current search and tag filters
 * so a tab shows what clicking it would actually return.
 */
export const GET_CLIENT_TAB_COUNTS = gql`
  query GetClientTabCounts(
    $all: ClientFilter
    $new: ClientFilter
    $prospects: ClientFilter
    $archived: ClientFilter
  ) {
    all: _allClientsMeta(filter: $all) {
      count
    }
    new: _allClientsMeta(filter: $new) {
      count
    }
    prospects: _allClientsMeta(filter: $prospects) {
      count
    }
    archived: _allClientsMeta(filter: $archived) {
      count
    }
  }
`;

export const GET_CLIENTS = gql`
  query GetClients(
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
    $filter: ClientFilter
  ) {
    allClients(
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
      filter: $filter
    ) {
      id
      name
      alias
      status
      notes
      createdAt
      updatedAt
      archivedAt
      peopleCount
      sessionsCount
      lifetimeSpend
      lastSessionAt
      nextSessionAt
      people {
        id
        name
        alias
        email
        phoneNumber
        archivedAt
        primaryClientId
        clients {
          id
          name
          alias
          status
          isPrimary
        }
        tags {
          id
          name
          slug
        }
      }
    }
    _allClientsMeta(filter: $filter) {
      count
    }
  }
`;

/**
 * Single-client detail query: computed stats, the member people (with their
 * other memberships, so the UI can show "also in ..."), and the Client's
 * bookings (attributed at creation time — Client-stable).
 */
export const GET_CLIENT_DETAIL = gql`
  query GetClientDetail($id: String!) {
    Client(id: $id) {
      id
      name
      alias
      status
      notes
      createdAt
      updatedAt
      archivedAt
      peopleCount
      sessionsCount
      lifetimeSpend
      lastSessionAt
      nextSessionAt
      people {
${PERSON_ROW_FIELDS}
      }
      bookings {
        id
        state
        method
        bookingTypeId
        createdAt
        updatedAt
        deletedAt
        bookingType {
          id
          name
        }
        availability {
          id
          date
          startTime
          endTime
          providerId
        }
      }
    }
  }
`;

/**
 * Paginated people list (the People sub-tab under Clients).
 */
export const GET_PEOPLE = gql`
  query GetPeople(
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
    $filter: PersonFilter
  ) {
    allPeople(
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
      filter: $filter
    ) {
${PERSON_ROW_FIELDS}
      updatedAt
      sessionsCount
      lifetimeSpend
      lastSessionAt
      nextSessionAt
    }
    _allPeopleMeta(filter: $filter) {
      count
    }
  }
`;

/**
 * Single-person detail (the person profile page): contact fields,
 * memberships, stats, and their bookings for the sessions list.
 */
export const GET_PERSON_DETAIL = gql`
  query GetPersonDetail($id: String!) {
    Person(id: $id) {
${PERSON_ROW_FIELDS}
      updatedAt
      sessionsCount
      lifetimeSpend
      lastSessionAt
      nextSessionAt
      bookings {
        id
        state
        method
        createdAt
        updatedAt
        deletedAt
        bookingType {
          id
          name
        }
        availability {
          id
          date
          startTime
          endTime
        }
      }
    }
  }
`;

/**
 * Notes aggregated across a set of people via the personIds filter (the
 * Client profile passes its member ids; the person profile passes one).
 * Notes stay attached to individual people. Newest first.
 */
export const GET_CLIENT_NOTES = gql`
  query GetClientNotes(
    $page: Int
    $perPage: Int
    $filter: PersonNoteFilter!
  ) {
    allPersonNotes(
      page: $page
      perPage: $perPage
      sortField: "createdAt"
      sortOrder: "DESC"
      filter: $filter
    ) {
      id
      personId
      authorId
      body
      authorEmail
      createdAt
      updatedAt
    }
    _allPersonNotesMeta(filter: $filter) {
      count
    }
  }
`;

/**
 * Check if an email already exists on a person, and which Clients that
 * person belongs to. Powers the soft duplicate warning AND the
 * attach-existing-person action on the add forms (with M:N membership,
 * adding an existing person to another client is a first-class move).
 */
export const CHECK_DUPLICATE_EMAIL = gql`
  query CheckDuplicateEmail($filter: PersonFilter) {
    allPeople(page: 0, perPage: 1, filter: $filter) {
      id
      name
      clients {
        id
        name
        alias
        isPrimary
      }
    }
  }
`;

/**
 * All person tags (for autocomplete vocabulary).
 * Full tag list sorted by name — small dataset, no pagination needed.
 */
export const GET_ALL_PERSON_TAGS = gql`
  query GetAllPersonTags {
    allPersonTags(
      page: 0
      perPage: 500
      sortField: "name"
      sortOrder: "ASC"
    ) {
      id
      name
      slug
    }
  }
`;

/**
 * Export-all query: same shape as allClients but with no pagination.
 * Used by "Export all (CSV)" and select-all-matching bulk actions.
 */
export const EXPORT_ALL_CLIENTS = gql`
  query ExportAllClients(
    $sortField: String
    $sortOrder: String
    $filter: ClientFilter
  ) {
    exportAllClients(
      sortField: $sortField
      sortOrder: $sortOrder
      filter: $filter
    ) {
      id
      name
      alias
      status
      notes
      createdAt
      updatedAt
      archivedAt
      peopleCount
      sessionsCount
      lifetimeSpend
      lastSessionAt
      nextSessionAt
      people {
        id
        name
        alias
        email
        phoneNumber
        archivedAt
        primaryClientId
        tags {
          id
          name
          slug
        }
      }
    }
  }
`;

/**
 * Lightweight client search for the move / merge / add-to-client pickers.
 * Active clients only, name-sorted, capped at 20 results.
 */
export const SEARCH_CLIENTS = gql`
  query SearchClients($filter: ClientFilter) {
    allClients(
      page: 0
      perPage: 20
      sortField: "name"
      sortOrder: "ASC"
      filter: $filter
    ) {
      id
      name
      alias
      peopleCount
    }
  }
`;

/**
 * Person picker options for the manual-session flow. Sessions are booked
 * for people, not Clients.
 */
export const GET_PERSON_OPTIONS = gql`
  query GetPersonOptions(
    $page: Int
    $perPage: Int
    $sortField: String
    $sortOrder: String
    $filter: PersonFilter
  ) {
    allPeople(
      page: $page
      perPage: $perPage
      sortField: $sortField
      sortOrder: $sortOrder
      filter: $filter
    ) {
      id
      name
      alias
      email
      primaryClientId
    }
  }
`;

// ── Mutations ──────────────────────────────────────────────────────────

/** Create the relationship record itself. People are added separately. */
export const CREATE_CLIENT = gql`
  mutation CreateClient(
    $name: String!
    $alias: String
    $notes: String
  ) {
    createClient(
      name: $name
      alias: $alias
      notes: $notes
    ) {
      id
      name
      alias
      status
      notes
      createdAt
      updatedAt
      archivedAt
    }
  }
`;

/**
 * Update the Client record (name/alias/notes, archive/unarchive via
 * archivedAt — the backend keeps `status` in sync).
 */
export const UPDATE_CLIENT = gql`
  mutation UpdateClient(
    $id: String!
    $name: String
    $alias: String
    $notes: String
    $archivedAt: DateTime
  ) {
    updateClient(
      id: $id
      name: $name
      alias: $alias
      notes: $notes
      archivedAt: $archivedAt
    ) {
      id
      name
      alias
      status
      notes
      createdAt
      updatedAt
      archivedAt
    }
  }
`;

/**
 * Bulk archive or unarchive Clients by ID list.
 */
export const ARCHIVE_CLIENTS = gql`
  mutation ArchiveClients(
    $ids: [String!]!
    $archive: Boolean!
  ) {
    archiveClients(
      ids: $ids
      archive: $archive
    ) {
      id
      name
      alias
      status
      archivedAt
    }
  }
`;

/**
 * Create a person. clientId omitted → the backend auto-creates a 1:1
 * Client around them; provided → they get a membership there instead.
 */
export const CREATE_PERSON = gql`
  mutation CreatePerson(
    $name: String!
    $email: String!
    $phoneNumber: String
    $alias: String
    $businessName: String
    $title: String
    $clientId: String
    $tagNames: [String]
  ) {
    createPerson(
      name: $name
      email: $email
      phoneNumber: $phoneNumber
      alias: $alias
      businessName: $businessName
      title: $title
      clientId: $clientId
      tagNames: $tagNames
    ) {
${PERSON_ROW_FIELDS}
    }
  }
`;

/** Update a person's own fields (per-person tags, archive via archivedAt). */
export const UPDATE_PERSON = gql`
  mutation UpdatePerson(
    $id: String!
    $name: String
    $email: String
    $phoneNumber: String
    $alias: String
    $businessName: String
    $title: String
    $archivedAt: DateTime
    $tagNames: [String]
  ) {
    updatePerson(
      id: $id
      name: $name
      email: $email
      phoneNumber: $phoneNumber
      alias: $alias
      businessName: $businessName
      title: $title
      archivedAt: $archivedAt
      tagNames: $tagNames
    ) {
${PERSON_ROW_FIELDS}
    }
  }
`;

// ── Membership mutations (client_people M:N) ─────────────────────────

/** Attach a person to a client. First membership becomes their primary. */
export const ADD_PERSON_TO_CLIENT = gql`
  mutation AddPersonToClient($personId: String!, $clientId: String!, $isPrimary: Boolean) {
    addPersonToClient(personId: $personId, clientId: $clientId, isPrimary: $isPrimary) {
      id
      primaryClientId
      clients {
        id
        name
        alias
        status
        isPrimary
      }
    }
  }
`;

/**
 * Detach a person from a client. Blocked on their last membership; removing
 * the primary promotes the oldest remaining one. History stays put.
 */
export const REMOVE_PERSON_FROM_CLIENT = gql`
  mutation RemovePersonFromClient($personId: String!, $clientId: String!) {
    removePersonFromClient(personId: $personId, clientId: $clientId) {
      id
      primaryClientId
      clients {
        id
        name
        alias
        status
        isPrimary
      }
    }
  }
`;

/** Mark one of the client's members as its primary person (main contact). */
export const SET_PRIMARY_PERSON = gql`
  mutation SetPrimaryPerson($personId: String!, $clientId: String!) {
    setPrimaryPerson(personId: $personId, clientId: $clientId) {
      id
      primaryClientId
      clients {
        id
        name
        alias
        status
        isPrimary
      }
    }
  }
`;

/**
 * Move a person's membership from one client to another (the 1:1 "person
 * changed relationships" case). Historical sessions/payments stay with the
 * original Client.
 */
export const MOVE_PERSON = gql`
  mutation MovePerson($personId: String!, $fromClientId: String!, $targetClientId: String!) {
    movePersonToClient(personId: $personId, fromClientId: $fromClientId, targetClientId: $targetClientId) {
      id
      primaryClientId
    }
  }
`;

/** Split a person out into a brand-new Client (history stays behind). */
export const SPLIT_PERSON = gql`
  mutation SplitPerson($personId: String!, $fromClientId: String, $name: String) {
    splitPersonIntoNewClient(personId: $personId, fromClientId: $fromClientId, name: $name) {
      id
      name
      alias
      status
    }
  }
`;

/**
 * Merge the source Client into the target. The target absorbs memberships,
 * payments, and sessions; the source is archived.
 */
export const MERGE_CLIENTS = gql`
  mutation MergeClients($sourceId: String!, $targetId: String!) {
    mergeClients(sourceId: $sourceId, targetId: $targetId) {
      id
      name
      alias
      status
      archivedAt
    }
  }
`;

// ── Note mutations (per-person) ──────────────────────────────────────

export const CREATE_PERSON_NOTE = gql`
  mutation CreatePersonNote(
    $personId: String!
    $body: String!
  ) {
    createPersonNote(
      personId: $personId
      body: $body
    ) {
      id
      personId
      authorId
      body
      authorEmail
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PERSON_NOTE = gql`
  mutation UpdatePersonNote(
    $id: String!
    $body: String!
  ) {
    updatePersonNote(
      id: $id
      body: $body
    ) {
      id
      personId
      authorId
      body
      authorEmail
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_PERSON_NOTE = gql`
  mutation DeletePersonNote($id: String!) {
    deletePersonNote(id: $id) {
      id
    }
  }
`;
